/**
 * Reports Page - View and download strategic analysis reports
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Loader2, Package, BarChart3, FileText, Download, FileSignature, FileSpreadsheet } from 'lucide-react';
import reportService, { ReportInfo, ReportContent } from '../services/reportService';
import businessPlanService from '../services/businessPlanService';
import financialForecastService from '../services/financialForecastService';
import apiClient from '../services/api';

interface BusinessContext {
  business_description?: string;
  objectives?: string[];
  timeline?: string;
  company_name?: string;
}

const ReportsPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  const [reports, setReports] = useState<ReportInfo[]>([]);
  const [selectedReport, setSelectedReport] = useState<ReportContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasBusinessPlan, setHasBusinessPlan] = useState(false);
  const [hasFinancialForecast, setHasFinancialForecast] = useState(false);
  const [businessContext, setBusinessContext] = useState<BusinessContext | null>(null);

  const loadDocumentStatus = async () => {
    // Load business context
    try {
      const ctx = await apiClient.get(`/api/sessions/${sessionId}/context`);
      setBusinessContext(ctx.data);
    } catch {
      // Non-critical
    }
    // Check business plan existence
    try {
      await businessPlanService.getBusinessPlan(sessionId!);
      setHasBusinessPlan(true);
    } catch {
      setHasBusinessPlan(false);
    }
    // Check financial forecast existence
    try {
      await financialForecastService.getMetadata(sessionId!);
      setHasFinancialForecast(true);
    } catch {
      setHasFinancialForecast(false);
    }
  };

  const loadReports = async () => {
    try {
      const data = await reportService.listReports(sessionId!);

      if (data.reports.length === 0) {
        // No reports yet, need to generate
        setLoading(false);
        return;
      }

      setReports(data.reports);

      // Auto-select executive summary if available
      const execSummary = data.reports.find(r => r.report_type === 'executive_summary');
      if (execSummary) {
        loadReport(execSummary.report_id);
      } else if (data.reports.length > 0) {
        loadReport(data.reports[0].report_id);
      }

      setLoading(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load reports');
      setLoading(false);
    }
  };

  const generateReports = async () => {
    setGenerating(true);
    setError(null);

    try {
      await reportService.generateReports(sessionId!);
      // Reload reports
      await loadReports();
      setGenerating(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to generate reports');
      setGenerating(false);
    }
  };

  const loadReport = async (reportId: string) => {
    setLoadingReport(true);
    try {
      const report = await reportService.getReport(sessionId!, reportId);
      setSelectedReport(report);
      setLoadingReport(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load report');
      setLoadingReport(false);
    }
  };

  useEffect(() => {
    loadReports();
    loadDocumentStatus();
  }, [sessionId]);

  const handleDownload = (reportId: string) => {
    const url = reportService.getDownloadUrl(sessionId!, reportId);
    window.open(url, '_blank');
  };

  const handleDownloadAll = () => {
    const url = reportService.getDownloadAllUrl(sessionId!);
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Loading reports...</h2>
      </div>
    );
  }

  if (error && reports.length === 0) {
    return (
      <div style={{ padding: '40px', maxWidth: '800px', margin: '0 auto' }}>
        <div style={{
          padding: '20px',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          border: '1px solid #f5c6cb',
          borderRadius: '4px',
        }}>
          <h3>Error</h3>
          <p>{error}</p>
          <button
            onClick={() => navigate('/')}
            style={{
              marginTop: '16px',
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  // No reports yet - show generate button
  if (reports.length === 0 && !generating) {
    return (
      <div style={{ padding: '40px', maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '16px' }}>Generate Strategic Analysis Reports</h1>
        <p style={{ fontSize: '1.1rem', color: '#666', marginBottom: '32px' }}>
          You've completed all Q&A sessions! Click below to generate your comprehensive strategic analysis reports.
        </p>

        <button
          onClick={generateReports}
          style={{
            padding: '16px 32px',
            fontSize: '1.1rem',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: 'bold',
          }}
        >
          Generate Reports
        </button>
      </div>
    );
  }

  if (generating) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{
          padding: '40px',
          backgroundColor: '#e3f2fd',
          border: '2px solid #2196f3',
          borderRadius: '8px',
          maxWidth: '600px',
          margin: '0 auto',
        }}>
          <h2 style={{ fontSize: '2rem', marginBottom: '16px' }}>Generating Reports...</h2>
          <p style={{ fontSize: '1.1rem', color: '#666' }}>
            Our specialist agents are compiling their strategic analyses. This will take just a moment.
          </p>
          <Loader2 size={40} style={{ marginTop: '24px', animation: 'spin 1s linear infinite' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 80px)', padding: '20px', gap: '20px' }}>
      {/* Sidebar - Report List */}
      <div style={{
        width: '300px',
        backgroundColor: '#f8f9fa',
        padding: '20px',
        borderRadius: '8px',
        overflowY: 'auto',
      }}>
        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          <img
            src="/coach_logo.png"
            alt="Coach"
            style={{ maxWidth: '180px', width: '100%' }}
          />
        </div>

        {businessContext && (
          <div style={{
            marginBottom: '16px',
            padding: '12px',
            backgroundColor: 'white',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            fontSize: '0.8rem',
            color: '#555',
            lineHeight: '1.5',
          }}>
            <div style={{ fontWeight: 'bold', fontSize: '0.85rem', color: '#1B2A4A', marginBottom: '6px' }}>
              {businessContext.company_name || 'Business Analysis'}
            </div>
            {businessContext.business_description && (
              <p style={{ margin: '0 0 6px 0' }}>
                {businessContext.business_description.length > 120
                  ? businessContext.business_description.slice(0, 120) + '...'
                  : businessContext.business_description}
              </p>
            )}
            {businessContext.timeline && (
              <div style={{ fontSize: '0.75rem', color: '#888' }}>
                Timeline: {businessContext.timeline}
              </div>
            )}
          </div>
        )}

        <div style={{ marginBottom: '20px' }}>
          <h2 style={{ fontSize: '1.3rem', marginBottom: '8px' }}>Reports</h2>
          <p style={{ fontSize: '0.9rem', color: '#666' }}>
            {reports.length} report{reports.length !== 1 ? 's' : ''} generated
          </p>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <button
            onClick={handleDownloadAll}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: 'bold',
            }}
          >
            <Package size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} /> Download All (ZIP)
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {reports.map((report) => (
            <div
              key={report.report_id}
              onClick={() => loadReport(report.report_id)}
              style={{
                padding: '12px',
                backgroundColor: selectedReport?.report_id === report.report_id ? '#007bff' : 'white',
                color: selectedReport?.report_id === report.report_id ? 'white' : '#333',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              <div style={{ fontSize: '0.9rem', fontWeight: 'bold', marginBottom: '4px' }}>
                {report.report_type === 'executive_summary'
                  ? <><BarChart3 size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Executive Summary</>
                  : <><FileText size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Agent Analysis</>
                }
              </div>
              {report.agent_id && (
                <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                  {report.agent_id.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Business Plan section */}
        <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {hasBusinessPlan ? (
            <>
              <button
                onClick={() => navigate(`/business-plan/${sessionId}`)}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#6f42c1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: 'bold',
                }}
              >
                <FileSignature size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} /> View Business Plan
              </button>
              <button
                onClick={() => window.open(businessPlanService.getDownloadUrl(sessionId!), '_blank')}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 'bold',
                }}
              >
                <Download size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} /> Download Business Plan (Word)
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate(`/business-plan-qa/${sessionId}`)}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#6f42c1',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: 'bold',
              }}
            >
              <FileSignature size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} /> Generate Business Plan
            </button>
          )}
        </div>

        {/* Financial Forecast section */}
        <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {hasFinancialForecast ? (
            <button
              onClick={() => window.open(financialForecastService.getDownloadUrl(sessionId!), '_blank')}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: 'bold',
              }}
            >
              <FileSpreadsheet size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} /> Download Forecast (.xlsx)
            </button>
          ) : hasBusinessPlan ? (
            <button
              onClick={() => navigate(`/financial-forecast-qa/${sessionId}`)}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: '#17a2b8',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: 'bold',
              }}
            >
              <FileSpreadsheet size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} /> Generate Financial Forecast
            </button>
          ) : null}
        </div>

        <div style={{ marginTop: '12px' }}>
          <button
            onClick={() => navigate('/')}
            style={{
              width: '100%',
              padding: '10px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9rem',
            }}
          >
            New Analysis
          </button>
        </div>
      </div>

      {/* Main Content - Report Viewer */}
      <div style={{
        flex: 1,
        backgroundColor: 'white',
        padding: '40px',
        borderRadius: '8px',
        overflowY: 'auto',
        border: '1px solid #dee2e6',
      }}>
        {loadingReport ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <h3>Loading report...</h3>
          </div>
        ) : selectedReport ? (
          <>
            <div style={{ marginBottom: '24px', paddingBottom: '16px', borderBottom: '2px solid #dee2e6' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div>
                  <h1 style={{ fontSize: '1.8rem', marginBottom: '8px' }}>
                    {selectedReport.agent_name || 'Strategic Analysis'}
                  </h1>
                  <div style={{ fontSize: '0.9rem', color: '#666' }}>
                    Generated: {new Date(selectedReport.generated_at).toLocaleString()}
                  </div>
                </div>
                <button
                  onClick={() => handleDownload(selectedReport.report_id)}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: 'bold',
                  }}
                >
                  <Download size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} /> Download Word Document
                </button>
              </div>
            </div>

            <div style={{
              fontSize: '1rem',
              lineHeight: '1.7',
              maxWidth: '100%',
            }}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({node, ...props}) => <h1 style={{ fontSize: '2rem', marginTop: '32px', marginBottom: '16px', color: '#2c3e50' }} {...props} />,
                  h2: ({node, ...props}) => <h2 style={{ fontSize: '1.6rem', marginTop: '28px', marginBottom: '12px', color: '#34495e' }} {...props} />,
                  h3: ({node, ...props}) => <h3 style={{ fontSize: '1.3rem', marginTop: '24px', marginBottom: '10px', color: '#4a5568' }} {...props} />,
                  p: ({node, ...props}) => <p style={{ marginBottom: '16px' }} {...props} />,
                  ul: ({node, ...props}) => <ul style={{ marginBottom: '16px', paddingLeft: '24px' }} {...props} />,
                  ol: ({node, ...props}) => <ol style={{ marginBottom: '16px', paddingLeft: '24px' }} {...props} />,
                  li: ({node, ...props}) => <li style={{ marginBottom: '8px' }} {...props} />,
                  strong: ({node, ...props}) => <strong style={{ fontWeight: 'bold', color: '#2d3748' }} {...props} />,
                  code: ({node, inline, ...props}: any) => inline ?
                    <code style={{ backgroundColor: '#f7fafc', padding: '2px 6px', borderRadius: '3px', fontSize: '0.9em' }} {...props} /> :
                    <code style={{ display: 'block', backgroundColor: '#2d3748', color: '#f7fafc', padding: '16px', borderRadius: '4px', overflowX: 'auto', fontSize: '0.9em' }} {...props} />,
                  blockquote: ({node, ...props}) => <blockquote style={{ borderLeft: '4px solid #4299e1', paddingLeft: '16px', marginLeft: '0', color: '#718096', fontStyle: 'italic' }} {...props} />,
                }}
              >
                {selectedReport.content}
              </ReactMarkdown>
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            <p>Select a report from the sidebar to view</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsPage;
